import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { DarkTheme, DefaultTheme, ThemeProvider } from "@-/com.npmjs.expo-router/react-navigation";
import { PortalHost as RnPrimitivesPortalHost } from "@rn-primitives/portal";
import { StatusBar } from "@-/com.npmjs.expo-status-bar";
import { PERSISTENT_KEYBOARD_TOOLBAR_PORTAL_HOST_NAME } from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Platform, Pressable, StyleSheet, View } from "@-/com.npmjs.react-native";
import { useUniwind } from "@-/com.npmjs.uniwind";
import { ReactNativeErrorBoundary } from "@-/com.npmjs.react-native-error-boundary";
import { yn } from "@-/com.npmjs.yn";
import { isDesktop } from "@-/com.npmjs.react-device-detect";
import { resetAllDatabases } from "@-/dev.improve.haniverse.lib.database.reset";
import { closeCachedActorDatabaseConnection } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  createRootQueryClient,
  rootQueryClientPersistOptions,
  subscribeOnlineManagerToNetworkState,
} from "@-/dev.improve.haniverse.lib.query-client";
import { restartApp } from "@-/dev.improve.haniverse.lib.app.restart";
import { KeyboardProvider } from "@-/com.npmjs.react-native-keyboard-controller";
import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import type { RootLayoutNavigationDevtoolsGate } from "@-/dev.improve.haniverse.components.rozenite";
import { QueryClient, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { PersistQueryClientProvider } from "@-/com.npmjs.tanstack__react-query-persist-client";

// Let an automation environment suppress the dev-only devtools overlay. Metro inlines
// `EXPO_PUBLIC_*` at transform time, so this costs nothing in a normal dev session (unset ⇒ the
// devtools mount exactly as before) and nothing at all in production (`__DEV__` is already false).
//
// WHY THIS EXISTS: on web the TanStack devtools mount a FULL-VIEWPORT transparent host in the root
// layout, painted after the app content. In dev-CI run 30286398873 that host swallowed the pointer
// events for the onboarding "Next" button — Maestro reported the tap COMPLETED (the element was
// found and clicked at its coordinates) while React's `onPress` NEVER ran, which a temporary
// `[welcome-next] press received` log proved by never appearing even though the app console was
// otherwise being captured. The result was a silent, un-loggable stall that survived three rounds of
// code-reading (hydration, the mutation's null guard, the navigator's isStillOnRoute guard were all
// investigated and cleared). Its devtools panel bounds `[0,757][1280,1157]` and trigger
// `[8,693][64,749]` sit directly over the bottom action bar at a 1280x757 viewport.
// `yn` accepts both "1" and "true". The `@type=boolean` coercion declared in `.env.schema` applies
// to the typed `ENV` proxy, NOT to a direct `process.env` read (which is what Expo's Metro
// transformer inlines), so the value arrives as whichever string the setter used.
// eslint-disable-next-line no-top/no-top-level-side-effects -- compile-time Metro-inlined env flag; must be module-scope for the lazy web-devtools branch below
const DEVTOOLS_DISABLED = yn(process.env.EXPO_PUBLIC_DISABLE_WEB_DEVTOOLS, { default: false });

// Only the web bundle ships the TanStack devtools panel; the native variant is a no-op. Crucially
// the dynamic `import()` must be unreachable on native — Metro pulls the async chunk into the
// native graph at build time even when the component never renders, and the resulting split-chunk
// reference fails to resolve on Hermes at load with `Requiring unknown module "<id>"`, redboxing
// the app on launch (which broke the Maestro flows). Keeping the `import()` inside this
// `Platform.OS === "web"` branch (which Metro constant-folds away on native) keeps it out of the
// native bundle entirely — do not hoist it into a standalone function.
//
// The panel loads through a normal query, so the async chunk only loads after hydration and never
// throws an import promise during server rendering. The query client is passed explicitly: the gate
// runs in `RootLayoutShell`, which owns the client but sits above its provider. `null` on a
// mobile-browser viewport, where the panel (and its event-bus toasts) overlaps the bottom tabs and
// offers no usable affordance.
const useLoadedWebTanstackDevtools: (
  enabled: boolean,
  queryClient: QueryClient,
) => ComponentType | null =
  Platform.OS === "web"
    ? (enabled, queryClient) => {
        const { data = null } = useQuery(
          {
            enabled,
            queryFn: async () => {
              const { WebTanstackDevtools } = await import("#web-tanstack-devtools");
              return isDesktop ? WebTanstackDevtools : null;
            },
            queryKey: ["webTanstackDevtoolsComponent"],
            staleTime: Number.POSITIVE_INFINITY,
          },
          queryClient,
        );
        return data ?? null;
      }
    : (_enabled, _queryClient) => null;

type WebTanstackDevtoolsGate = Tagged<{ Devtools: ComponentType }, "WebTanstackDevtoolsGate">;

// Mount-gate the asynchronously loaded devtools so they NEVER render during server rendering (web
// `EXPO_WEB_OUTPUT=server`). Expo Router SSRs pages with React's `renderToString`, which does not
// support Suspense, so the module loads through `useLoadedWebTanstackDevtools` only after
// hydration. `null` = hydrating, disabled for automation, still loading, or a mobile viewport.
function useEnabledWebTanstackDevtoolsGate(
  queryClient: QueryClient,
): WebTanstackDevtoolsGate | null {
  const hasHydrated = useHasHydrated();
  // The module-scope `DEVTOOLS_DISABLED` is the primary automation gate, but it is evaluated once
  // when this module is first imported; reading `process.env` again at render time is the
  // belt-and-braces check that survives whatever order Metro/varlock populate the runtime env
  // object in. Cheap, and the alternative is a devtools overlay that silently eats pointer events
  // during a Maestro run. Both are values here, never a hook selection: the hook list must not
  // depend on anything but the Metro-inlined `__DEV__` literal (see `routes/root.tsx`).
  const disabled =
    DEVTOOLS_DISABLED || yn(process.env.EXPO_PUBLIC_DISABLE_WEB_DEVTOOLS, { default: false });
  const Devtools = useLoadedWebTanstackDevtools(hasHydrated && !disabled, queryClient);
  return hasHydrated && !disabled && Devtools !== null
    ? createGate<WebTanstackDevtoolsGate>({ Devtools })
    : null;
}

function useNoWebTanstackDevtoolsGate(_queryClient: QueryClient): WebTanstackDevtoolsGate | null {
  return null;
}

// Dev-only. `__DEV__` is a Metro-inlined literal, so the selected hook is fixed for a given bundle.
const useWebTanstackDevtoolsGate = __DEV__
  ? useEnabledWebTanstackDevtoolsGate
  : useNoWebTanstackDevtoolsGate;

function WebTanstackDevtoolsHost({ gate: { Devtools } }: { gate: WebTanstackDevtoolsGate }) {
  return <Devtools />;
}

/**
 * The portal hosts, mounted only after hydration (see `useHasHydrated`).
 *
 * `react-native-teleport`'s `PortalHost` renders a raw `<div>` with an inline `style` object and
 * IGNORES the `className` it is given (see its `lib/module/views/PortalHost`). React's DOM server
 * serializes that style as CSS (`pointer-events:none`) while the client keeps the React prop
 * (`pointerEvents`), so the two trees disagree on the very first node — React reports "server
 * rendered HTML didn't match the client", throws away the ENTIRE server tree and re-renders from
 * scratch. Because the app's styles are injected by the client bundle, that regeneration is exactly
 * the flash of unstyled content the hydration gate prevents.
 *
 * Nothing is lost by skipping them on the server: both hosts exist purely to receive portaled
 * interactive UI (the persistent-keyboard toolbar and rn-primitives overlays), which can only
 * appear after hydration anyway.
 */
function PortalHosts() {
  return (
    <>
      <View className="absolute inset-0" style={styles.portalHostContainer}>
        <PortalHost
          className="absolute inset-0"
          name={PERSISTENT_KEYBOARD_TOOLBAR_PORTAL_HOST_NAME}
        />
      </View>
      <RnPrimitivesPortalHost />
    </>
  );
}
import { Toaster } from "@-/com.npmjs.sonner";
import { ErrorDetailsSheet } from "@-/dev.improve.haniverse.components.ui.error-details-sheet";
import { BottomSheetModalProvider } from "@-/dev.improve.haniverse.components.ui.bottom-sheet-provider";
import { GestureHandlerRootView } from "@-/com.npmjs.react-native-gesture-handler";
import { MetroBridgeProfilerRenderer } from "@-/dev.improve.haniverse.components.metro-bridge-profiler";
import { useNavigationContainerRef } from "@-/com.npmjs.expo-router";
import { PortalHost, PortalProvider } from "@-/com.npmjs.react-native-teleport";
import changeNavigationBarColor from "@-/com.npmjs.react-native-navigation-bar-color-kt";
import { FiberProvider } from "@-/com.npmjs.its-fine";
import { DeviceEdgeSafeAreaInsetsProvider } from "@-/dev.improve.haniverse.lib.device-edge-safe-area";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * How long "Retrying…" may wait for the cached actor connection to close. Closing drains in-flight
 * statements, which are local and settle in milliseconds; 8s is far past any healthy drain. A close
 * that outlives it means the connection itself is wedged, and re-opening in process cannot help
 * while it is — so the deadline takes the same exit as a failed close: a restart.
 */
const ERROR_RECOVERY_CLOSE_DEADLINE_MS = 8000;

const styles = StyleSheet.create({
  portalHostContainer: {
    pointerEvents: "box-none",
  },
  root: {
    flex: 1,
  },
});

function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  const [resetStatus, setResetStatus] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "error"; message: string }
    | { kind: "done"; removedFiles: string[] }
  >({ kind: "idle" });
  const [isRecovering, setIsRecovering] = useState(false);

  // Recover IN PROCESS rather than betting the app on a reload that this build may
  // not be able to perform.
  //
  // Re-mounting alone isn't enough: the module-level actor DB caches survive a
  // remount by design (`use-actor-database.ts` keeps the handle across Strict
  // Mode / Suspense remounts), so a remount would hand the new tree the very
  // handle that just failed. Dropping the cached connection first — via the
  // close-safe `closeCachedActorDatabaseConnection`, which drains in-flight
  // statements before `sqlite3_close` — means `resetError()` re-opens from
  // scratch. TanStack Query refetches errored queries when a fresh observer
  // mounts (`retryOnMount`), so the suspense reads re-run on their own.
  //
  // Only if that recovery path itself throws — or the close outlives
  // `ERROR_RECOVERY_CLOSE_DEADLINE_MS` — do we fall back to a restart.
  const handleTryAgain = () => {
    setIsRecovering(true);
    void (async () => {
      try {
        await withDeadline(
          {
            deadline: ERROR_RECOVERY_CLOSE_DEADLINE_MS,
            operation: "root error recovery: close cached actor database connection",
          },
          () => closeCachedActorDatabaseConnection(),
        );
        // Unmounts this fallback, so no need to clear `isRecovering` on success.
        resetError();
      } catch (recoveryError) {
        logger(import.meta.location).error(
          "In-process recovery failed; restarting the app",
          recoveryError,
        );
        setIsRecovering(false);
        void restartApp();
      }
    })();
  };

  const handleResetDatabase = () => {
    setResetStatus({ kind: "running" });
    void (async () => {
      try {
        const result = await resetAllDatabases();
        setResetStatus({ kind: "done", removedFiles: result.removedFiles });
      } catch (error) {
        const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        logger(import.meta.location).error("Reset database (dev) failed", error);
        setResetStatus({ kind: "error", message });
      }
    })();
  };

  return (
    <View className="flex-1 items-center justify-center gap-4 p-8">
      <Text className="text-lg font-semibold">Something went wrong</Text>
      <Text className="text-center text-[13px] opacity-60">{error.message}</Text>
      <Pressable
        className="mt-2 rounded-xs bg-red-500 px-6 py-2.5"
        disabled={isRecovering}
        onPress={handleTryAgain}
      >
        <Text className="font-semibold text-white">{isRecovering ? "Retrying…" : "Try again"}</Text>
      </Pressable>
      {/* Always available, in every build: if the in-process recovery above can't
          clear the failure, the user still has an escape hatch that doesn't depend
          on expo-updates being configured. */}
      <Pressable
        className="rounded-xs border border-foreground/20 px-6 py-2.5"
        onPress={() => {
          void restartApp();
        }}
      >
        <Text className="font-semibold opacity-70">Restart app</Text>
      </Pressable>
      {__DEV__ ? (
        <>
          <Pressable
            className="rounded-xs border border-red-500 px-6 py-2.5"
            disabled={resetStatus.kind === "running"}
            onPress={handleResetDatabase}
          >
            <Text className="font-semibold text-red-500">
              {resetStatus.kind === "running" ? "Resetting…" : "Reset database (dev)"}
            </Text>
          </Pressable>
          {resetStatus.kind === "done" ? (
            <Text className="text-center text-[12px] opacity-60">
              Removed:{" "}
              {hasLength(resetStatus.removedFiles, 0)
                ? "(no files existed)"
                : resetStatus.removedFiles.join(", ")}
              . Reloading…
            </Text>
          ) : null}
          {resetStatus.kind === "error" ? (
            <Text className="text-center text-[12px] text-red-500">
              Reset failed: {resetStatus.message}
            </Text>
          ) : null}
        </>
      ) : null}
    </View>
  );
}

export type RootLayoutDevtoolsProps = {
  navigationRef: ReturnType<typeof useNavigationContainerRef>;
  queryClient: QueryClient;
};

export type RootLayoutNavigationDevtoolsProps = Pick<RootLayoutDevtoolsProps, "navigationRef"> & {
  gate: RootLayoutNavigationDevtoolsGate;
};

export type UseRootLayoutNavigationDevtoolsGate = () => RootLayoutNavigationDevtoolsGate | null;

export type RootLayoutShellDevtoolsProps = Pick<RootLayoutDevtoolsProps, "queryClient">;

export type UseRootLayoutShellDevtools = (args: RootLayoutShellDevtoolsProps) => void;

function useNoopRootLayoutShellDevtools(_args: RootLayoutShellDevtoolsProps) {}

function useNoRootLayoutNavigationDevtoolsGate(): RootLayoutNavigationDevtoolsGate | null {
  return null;
}

export function RootLayoutProviders({
  children,
  NavigationDevtools,
  useNavigationDevtoolsGate,
  useShellDevtools,
}: {
  children: ReactNode;
  NavigationDevtools?: ComponentType<RootLayoutNavigationDevtoolsProps>;
  useNavigationDevtoolsGate?: UseRootLayoutNavigationDevtoolsGate;
  useShellDevtools?: UseRootLayoutShellDevtools;
}) {
  if (__DEV__) {
    logger(import.meta.location).log("layout: RootLayoutProviders render");
  }
  const navigationRef = useNavigationContainerRef();
  // Supplied only by the `__DEV__`-required devtools bundle, so the hook is constant per bundle.
  const navigationDevtoolsGate = (
    useNavigationDevtoolsGate ?? useNoRootLayoutNavigationDevtoolsGate
  )();

  return (
    // `BottomSheetModalProvider` must be an ANCESTOR of `PortalProvider`, not a descendant: the
    // legal-consent AlertDialog on the auth gate (@rn-primitives/alert-dialog) PORTALS its content
    // to the `PortalProvider` host, and that content mounts `LegalDocumentSheet` (a @gorhom
    // `BottomSheetModal`). If `BottomSheetModalProvider` sits below `PortalProvider`, the portaled
    // sheet renders outside the provider and redboxes with `'BottomSheetModalInternalContext'
    // cannot be null!`, taking down the whole "Continue without an account" dialog (guest sign-in).
    // `BottomSheetModalProvider` itself needs a `GestureHandlerRootView` ancestor, so the order is
    // GestureHandlerRootView → BottomSheetModalProvider → PortalProvider → app tree.
    // `DeviceEdgeSafeAreaInsetsProvider` sits above all of them so sheets, portals and toasts
    // read the same sensor-housing-aware landscape insets as the app tree.
    <FiberProvider>
      <DeviceEdgeSafeAreaInsetsProvider>
        <GestureHandlerRootView className="flex-1 bg-background" style={styles.root}>
          <BottomSheetModalProvider>
            <PortalProvider>
              <ReactNativeErrorBoundary FallbackComponent={ErrorFallback}>
                <RootLayoutShell useShellDevtools={useShellDevtools}>
                  <MetroBridgeProfilerRenderer id="app">{children}</MetroBridgeProfilerRenderer>
                </RootLayoutShell>
              </ReactNativeErrorBoundary>
              <Toaster />
              {__DEV__ && NavigationDevtools !== undefined && navigationDevtoolsGate !== null && (
                <NavigationDevtools gate={navigationDevtoolsGate} navigationRef={navigationRef} />
              )}
            </PortalProvider>
          </BottomSheetModalProvider>
        </GestureHandlerRootView>
      </DeviceEdgeSafeAreaInsetsProvider>
    </FiberProvider>
  );
}

function RootLayoutShell({
  children,
  useShellDevtools,
}: {
  children: ReactNode;
  useShellDevtools?: UseRootLayoutShellDevtools;
}) {
  const [queryClient] = useState(createRootQueryClient);
  (useShellDevtools ?? useNoopRootLayoutShellDevtools)({ queryClient });
  const hasHydrated = useHasHydrated();
  const webTanstackDevtoolsGate = useWebTanstackDevtoolsGate(queryClient);

  // Feed real device connectivity into `onlineManager`. Matters most now that `online` is the
  // default static-data mode: without it React Native reports permanently online, so queries
  // never refetch on reconnect. It never pauses work (the root client uses `networkMode:
  // "always"`). No-op on web (browser events already drive the default manager).
  useEffect(() => subscribeOnlineManagerToNetworkState(), []);

  useEffect(() => {
    if (Platform.OS !== "android") {
      return;
    }
    try {
      changeNavigationBarColor("#000000", false);
    } catch (error: unknown) {
      logger(import.meta.location).warn("Android navigation bar color update failed", error);
    }
  }, []);

  return (
    // No `SafeAreaProvider` here on purpose: Expo Router already mounts one at the app root
    // (`expo-router/build/ExpoRoot.js` wraps the whole tree in `<SafeAreaProvider initialMetrics>`),
    // so adding a second one just nests a redundant `RNCSafeAreaProvider` native view under it and
    // deepens every screen's hierarchy by a level. React Navigation's own `SafeAreaProviderCompat`
    // follows the same rule — it skips wrapping when `SafeAreaInsetsContext` already has insets.
    // Every `useSafeAreaInsets()` here resolves against Expo Router's provider with identical values.
    <KeyboardProvider>
      <View className="flex-1" style={styles.root}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={rootQueryClientPersistOptions()}
        >
          {webTanstackDevtoolsGate !== null && (
            <WebTanstackDevtoolsHost gate={webTanstackDevtoolsGate} />
          )}
          <RootNavigationTheme>
            <StatusBar style="auto" />
            {children}
            <ErrorDetailsSheet />
            {hasHydrated && <PortalHosts />}
          </RootNavigationTheme>
        </PersistQueryClientProvider>
      </View>
    </KeyboardProvider>
  );
}

/**
 * Baseline navigation theme for routes outside `NavContext` (e.g. modal). Follows Uniwind's
 * resolved theme (driven by the device `theme` setting) rather than the raw OS color scheme, so
 * the navigation card/header background can't diverge from the `.dark`-class UI when the user has
 * picked an explicit Light/Dark override that differs from the system appearance.
 */
function RootNavigationTheme({ children }: { children: ReactNode }) {
  const { theme: uniwindTheme } = useUniwind();
  const navigationTheme = uniwindTheme === "dark" ? DarkTheme : DefaultTheme;

  return (
    <ThemeProvider value={navigationTheme}>{children}</ThemeProvider>
  );
}
